import type { ComboboxParsedItem, OptionsFilter } from "@mantine/core";

// Decomposed and stripped of its marks, so a needle typed on a keyboard with no diacritics still finds the haystack.
export function fold(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export const NO_MATCH = Number.MAX_SAFE_INTEGER;

// Three pickers search more text than their labels hold, and each has to order what that finds or the answer arrives
// under everything that merely matched. The bands are each picker's own; this is the sorting and dropping they share.
export function rankedFilter(
  rankOf: (value: string, needle: string) => number,
  needleOf: (search: string) => string = fold,
): OptionsFilter {
  return ({ options, search }) => {
    const needle = needleOf(search.trim());
    if (!needle) return options;

    return options
      .flatMap((option) => {
        const rank = isItem(option) ? rankOf(String(option.value), needle) : NO_MATCH;
        return rank === NO_MATCH ? [] : [{ option, rank }];
      })
      // Stable, so the order the options arrive in survives within each rank.
      .sort((a, b) => a.rank - b.rank)
      .map((match) => match.option);
  };
}

// What a country and a time zone are both searched by: the name on the label, the codes only an exact typing reaches,
// the names only an exact typing ranks, and the rest of what it answers to. A picker whose bands are not these ranks
// itself and shares the rest of this.
export interface SearchTerms {
  name: string;
  codes: Set<string>;
  // What the label says whole, which is the label itself and, for a zone, each part of it between the slashes.
  whole: Set<string>;
  // Everything else it is called, each name whole: the Netherlands answers to Holland, and a zone to its country.
  aliases: Set<string>;
  // Best band first, and a needle scores by the first of them it turns up in: a country is one band of everything
  // else it is called, where a zone keeps where it is apart from what its clock reads.
  rest: string[];
}

// A code typed in full is somebody reading it off a spreadsheet or a clock, and the one thing they cannot have meant
// loosely; then a name typed in full, the label's own before any other, since UK is all of one of the United
// Kingdom's names and only the start of Ukraine's; then the label from its start, then the label at all, then each
// band of what else the option goes by.
export function termRank(terms: SearchTerms | undefined, needle: string): number {
  if (!terms) return NO_MATCH;
  if (terms.codes.has(needle)) return 0;
  if (terms.whole.has(needle)) return 1;
  if (terms.aliases.has(needle)) return 2;
  if (terms.name.startsWith(needle)) return 3;
  if (terms.name.includes(needle)) return 4;

  const band = terms.rest.findIndex((text) => text.includes(needle));
  return band === -1 ? NO_MATCH : 5 + band;
}

function isItem(option: ComboboxParsedItem): option is Extract<ComboboxParsedItem, { value: unknown }> {
  return "value" in option;
}
